import { X } from 'lucide-react';
import { useId, useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Input } from '@/modules/ui/components/input/Input';
import { Label } from '@/modules/ui/components/label/Label';

interface TagInputProps {
  value: string[];
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  addDisabled?: boolean;
  formatTag?: (tag: string) => string;
  onChange: (tags: string[]) => void;
}

export function TagInput({
  value,
  label,
  placeholder = 'Add tags (press Enter or comma to add)',
  onChange,
  className,
  disabled = false,
  addDisabled = false,
  formatTag,
}: TagInputProps) {
  const inputId = useId();
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();

      const trimmedValue = inputValue.trim();
      if (
        !disabled &&
        !addDisabled &&
        trimmedValue &&
        !value.some(
          (tag) => tag.trim().toLowerCase() === trimmedValue.toLowerCase(),
        )
      ) {
        onChange([...value, trimmedValue]);
        setInputValue('');
      }
    }
  };

  const handleRemoveTag = (tag: string) => {
    if (!disabled) {
      onChange(value.filter((t) => t !== tag));
    }
  };

  return (
    <div className={cn('space-y-4', className)}>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <div className="space-y-4">
        <Input
          id={inputId}
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || addDisabled}
        />

        {value.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {value.map((tag) => {
              const formattedTag = formatTag?.(tag) ?? tag;
              return (
                <Badge key={tag} variant="secondary" className="pr-1">
                  {formattedTag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    disabled={disabled}
                    aria-label={`Remove ${formattedTag}`}
                    className="hover:bg-muted ml-1 cursor-pointer rounded-sm disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </Badge>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
